import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, ArrowRight } from "lucide-react";
import { AuthForm } from "../components/AuthForm";
import { inputCls, labelCls } from "../components/ShopUI";
import { createCheckout } from "../lib/api";
import { money, useShop } from "../context/ShopContext";

const STATES = ["NSW", "VIC", "QLD", "WA", "SA", "TAS", "ACT", "NT"];

const Summary = ({ cart, subtotal, deliveryFee, total }) => (
  <aside className="border border-cream-border bg-[#fbf8f1] p-8 h-max" data-testid="checkout-summary">
    <h2 className="font-serif text-2xl text-ink">Your order</h2>
    <div className="mt-7 space-y-5">
      {cart.map((l, i) => (
        <div key={i} className="flex gap-4" data-testid={`checkout-line-${i}`}>
          <img src={l.image} alt="" className="w-14 h-14 object-cover bg-cream-alt shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm text-ink">{l.name} <span className="text-ink-muted">× {l.quantity}</span></p>
            {l.scents?.length > 0 && <p className="text-[0.65rem] text-ink-muted mt-1">{l.scents.join(" · ")}</p>}
          </div>
          <span className="text-sm text-ink shrink-0">{money(l.price * l.quantity)}</span>
        </div>
      ))}
    </div>
    <div className="mt-7 pt-6 border-t border-cream-border space-y-3 text-sm">
      <div className="flex justify-between text-ink-muted"><span>Subtotal</span><span className="text-ink">{money(subtotal)}</span></div>
      <div className="flex justify-between text-ink-muted"><span>Delivery</span><span className="text-ink" data-testid="checkout-delivery">{deliveryFee === 0 ? "Free" : money(deliveryFee)}</span></div>
    </div>
    <div className="flex justify-between items-baseline border-t border-cream-border mt-6 pt-6">
      <span className="text-[0.65rem] uppercase tracking-[0.18em] text-ink-muted">Total</span>
      <span className="font-serif text-3xl text-ink" data-testid="checkout-total">{money(total)}</span>
    </div>
  </aside>
);

export default function Checkout() {
  const navigate = useNavigate();
  const { cart, subtotal, deliveryFee, total, customer, token, hasSubscription } = useShop();
  const [form, setForm] = useState({
    full_name: "", phone: "", line1: "", line2: "",
    suburb: "", state: "NSW", postcode: "", country: "AU", notes: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  if (cart.length === 0) {
    return (
      <div className="min-h-[70vh] grid place-items-center bg-cream px-6" data-testid="checkout-empty">
        <div className="text-center">
          <h1 className="font-serif text-4xl text-ink">Nothing to check out yet</h1>
          <Link to="/shop" className="inline-flex items-center gap-2 mt-6 text-gold text-xs uppercase tracking-[0.2em]">
            Browse the shop <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    );
  }

  const pay = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await createCheckout({
        items: cart.map((l) => ({ product_id: l.product_id, quantity: l.quantity, scents: l.scents })),
        shipping: { ...form, full_name: form.full_name || customer.name },
        notes: form.notes,
        origin_url: window.location.origin,
      }, token);
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "We couldn't start the payment. Please try again.");
      setBusy(false);
    }
  };

  return (
    <div className="bg-cream min-h-[80vh]" data-testid="checkout-page">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-32 md:pt-40 pb-24">
        <h1 className="font-serif text-4xl md:text-5xl tracking-tight text-ink">Checkout</h1>
        <span className="block w-14 h-px bg-gold mt-5" />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 mt-12">
          <div className="lg:col-span-2">
            {!customer ? (
              <AuthForm
                heading="Create your account to continue"
                blurb="We keep your order history and delivery details against your account, so reordering refills takes a few seconds."
              />
            ) : (
              <form onSubmit={pay} data-testid="shipping-form">
                <div className="border border-cream-border bg-[#fbf8f1] px-6 py-4 flex items-center justify-between gap-4 mb-8">
                  <p className="text-sm text-ink-muted">
                    Ordering as <span className="text-ink">{customer.name}</span> ({customer.email})
                  </p>
                  <Link to="/account" className="text-xs uppercase tracking-[0.15em] text-gold shrink-0" data-testid="checkout-account-link">Account</Link>
                </div>

                <h2 className="font-serif text-2xl text-ink mb-6">Delivery address</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className={labelCls}>Full name</label>
                    <input className={inputCls} value={form.full_name} onChange={(e) => set("full_name", e.target.value)} required data-testid="ship-name" />
                  </div>
                  <div>
                    <label className={labelCls}>Phone</label>
                    <input className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="ship-phone" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls}>Street address</label>
                    <input className={inputCls} value={form.line1} onChange={(e) => set("line1", e.target.value)} required data-testid="ship-line1" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls}>Apartment / unit (optional)</label>
                    <input className={inputCls} value={form.line2} onChange={(e) => set("line2", e.target.value)} data-testid="ship-line2" />
                  </div>
                  <div>
                    <label className={labelCls}>Suburb</label>
                    <input className={inputCls} value={form.suburb} onChange={(e) => set("suburb", e.target.value)} required data-testid="ship-suburb" />
                  </div>
                  <div className="grid grid-cols-2 gap-5">
                    <div>
                      <label className={labelCls}>State</label>
                      <select className={inputCls} value={form.state} onChange={(e) => set("state", e.target.value)} data-testid="ship-state">
                        {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className={labelCls}>Postcode</label>
                      <input className={inputCls} value={form.postcode} onChange={(e) => set("postcode", e.target.value)} required data-testid="ship-postcode" />
                    </div>
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls}>Delivery notes (optional)</label>
                    <textarea rows={2} className={`${inputCls} resize-none`} value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="ship-notes" />
                  </div>
                </div>

                <button type="submit" disabled={busy} data-testid="pay-button"
                  className="w-full mt-9 inline-flex items-center justify-center gap-3 px-8 py-5 bg-[#221d17] text-cream text-xs font-semibold uppercase tracking-[0.2em] hover:bg-black transition-colors disabled:opacity-60">
                  <Lock size={14} /> {busy ? "Redirecting to payment…" : hasSubscription ? `Start plan — ${money(total)} / month` : `Pay ${money(total)} securely`}
                </button>
                <p className="text-[0.65rem] text-ink-muted mt-4 text-center">
                  {hasSubscription
                    ? "You'll be taken to Stripe to set up the monthly plan. Cancel any time from your account."
                    : "You'll be taken to Stripe to pay. We never see or store your card details."}
                </p>
                <button type="button" onClick={() => navigate("/cart")} className="block mx-auto mt-5 text-xs uppercase tracking-[0.18em] text-ink-muted hover:text-gold transition-colors">
                  Back to bag
                </button>
              </form>
            )}
          </div>
          <Summary cart={cart} subtotal={subtotal} deliveryFee={deliveryFee} total={total} />
        </div>
      </div>
    </div>
  );
}
